import React, { useState, useEffect, useCallback, useRef } from "react";
import { Modal } from "antd";
import {
  LeftOutlined,
  RightOutlined,
  CloseOutlined,
  PlusOutlined,
  MinusOutlined,
  RedoOutlined,
} from "@ant-design/icons";
import color from "../../resource/color";
import Text from "../Text/Text";

interface ImagePreviewModalProps {
  open: boolean;
  images?: string[];
  onClose: () => void;
  initialIndex?: number;
}

const IMAGE_WIDTH = 996;
const IMAGE_HEIGHT = 473;

const ZOOM_STEP = 0.2;
const ZOOM_MIN = 1;
const ZOOM_MAX = 3;

export const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({
  open,
  images = [],
  onClose,
  initialIndex = 0,
}) => {
  const [index, setIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });

  const containerRef = useRef<HTMLDivElement>(null);

  const hasMultiple = images.length > 1;

  /* reset when open */
  useEffect(() => {
    if (!open) return;

    setIndex(initialIndex);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  }, [open, initialIndex]);

  /* zoom helpers */
  const zoomIn = useCallback(() => {
    setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)));
  }, []);

  const zoomOut = useCallback(() => {
    setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)));
  }, []);

  const resetZoom = useCallback(() => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  /* navigation */
  const next = useCallback(() => {
    setIndex((i) => (i + 1) % images.length);
    resetZoom();
  }, [images.length, resetZoom]);

  const prev = useCallback(() => {
    setIndex((i) => (i - 1 + images.length) % images.length);
    resetZoom();
  }, [images.length, resetZoom]);

  useEffect(() => {
    if (!open) return;

    const map: Record<string, () => void> = {
      ArrowRight: next,
      ArrowLeft: prev,
      Escape: onClose,
      "+": zoomIn,
      "=": zoomIn,
      "-": zoomOut,
      "0": resetZoom,
    };

    const handleKey = (e: KeyboardEvent) => map[e.key]?.();

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, next, prev, onClose, zoomIn, zoomOut, resetZoom]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const wheel = (e: WheelEvent) => {
      e.preventDefault();
      e.deltaY < 0 ? zoomIn() : zoomOut();
    };

    el.addEventListener("wheel", wheel, { passive: false });
    return () => el.removeEventListener("wheel", wheel);
  }, [zoomIn, zoomOut]);

  const onMouseDown = (e: React.MouseEvent) => {
    if (zoom === 1) return;

    setDragging(true);

    dragStart.current = {
      x: e.clientX - offset.x,
      y: e.clientY - offset.y,
    };
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (!dragging) return;

    setOffset({
      x: e.clientX - dragStart.current.x,
      y: e.clientY - dragStart.current.y,
    });
  };

  const onMouseUp = () => setDragging(false);

  const onDoubleClick = () => {
    zoom === 1 ? setZoom(2) : resetZoom();
  };

  if (!images.length) return null;

  return (
    <Modal
      open={open}
      footer={null}
      closable={false}
      centered
      width='auto'
      onCancel={onClose}
      maskClosable={false}
      style={{ top: 0 }}
      styles={{
        body: { padding: 0, background: "transparent" },
        content: { background: "transparent", boxShadow: "none" },
      }}
      maskStyle={{
        background: "rgba(0,0,0,0.75)",
        backdropFilter: "blur(3px)",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "80px 120px",
          position: "relative",
        }}
      >
        <CloseOutlined
          onClick={onClose}
          style={{
            position: "absolute",
            top: 20,
            right: 20,
            fontSize: 18,
            color: "#fff",
            background: "rgba(0,0,0,.6)",
            padding: 10,
            borderRadius: "50%",
            cursor: "pointer",
            zIndex: 10,
          }}
        />
        <div
          ref={containerRef}
          style={{
            width: IMAGE_WIDTH,
            height: IMAGE_HEIGHT,
            background: "#2b2b31",
            padding: 4,
            overflow: "hidden",
            borderRadius: 8,
            cursor: zoom === 1 ? "default" : dragging ? "grabbing" : "grab",
            position: "relative",
          }}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          onDoubleClick={onDoubleClick}
        >
          <img
            src={images[index]}
            draggable={false}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              userSelect: "none",
              transform: `translate(${offset.x}px,${offset.y}px) scale(${zoom})`,
              transition: dragging ? "none" : "transform .2s ease",
            }}
          />
          <div
            style={{
              position: "absolute",
              top: 12,
              left: 12,
              display: "flex",
              gap: 10,
              background: "rgba(0,0,0,.55)",
              padding: "6px 12px",
              borderRadius: 8,
              alignItems: "center",
            }}
          >
            <MinusOutlined onClick={zoomOut} style={{ color: "#fff", cursor: "pointer" }} />
            <Text color='white'>{Math.round(zoom * 100)}%</Text>
            <PlusOutlined onClick={zoomIn} style={{ color: "#fff", cursor: "pointer" }} />
            <RedoOutlined onClick={resetZoom} style={{ color: "#fff", cursor: "pointer" }} />
          </div>
        </div>

        {/* CAROUSEL NAV */}
        {hasMultiple && (
          <div
            style={{
              marginTop: 20,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 10,
            }}
          >
            <Text color='white'>
              {index + 1}/{images.length}
            </Text>

            <div
              style={{
                display: "flex",
                gap: 40,
                fontSize: 20,
                color: color.white,
                background: "rgba(0,0,0,.55)",
                padding: "8px 24px",
                borderRadius: 20,
              }}
            >
              <LeftOutlined onClick={prev} style={{ cursor: "pointer" }} />
              <RightOutlined onClick={next} style={{ cursor: "pointer" }} />
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};
